import { type ReceiverType, ReceiverTypes } from '../../common/utils/billingAccountUtils.js';
import { BillingAccountRelatedPartyRole, type BillingAccountType } from '../../common/utils/billingAccountUtilsCoCo.js';
import { BillingAccountDeliveryMethod } from '../../generated/api/billingAccountDeliveryMethod.js';
import { ContactMedium } from '../../generated/api/contactMedium.js';
import { ContactType } from '../../generated/api/contactType.js';
import { CustomerType } from '../../generated/api/customerType.js';
import type { BillDeliveryMethod } from '../../generated/api/billDeliveryMethod.js';
import type { BillFormat } from '../../generated/api/billFormat.js';
import type { BillingAccountCreateRequestCoco } from '../../generated/api/billingAccountCreateRequestCoco.js';
import type { Contact } from '../../generated/api/contact.js';
import type { CustomerResponse } from '../../generated/api/customerResponse.js';
import type { MediumCharacteristic } from '../../generated/api/mediumCharacteristic.js';
import type { RelatedParty } from '../../generated/api/relatedParty.js';
import type { RelatedPartyRef } from '../../generated/api/relatedPartyRef.js';

const USER_EMAIL_MEDIUM_TYPES = [
  ContactMedium.MediumTypeEnum.User1Email,
  ContactMedium.MediumTypeEnum.User2Email,
  ContactMedium.MediumTypeEnum.User3Email,
  ContactMedium.MediumTypeEnum.User4Email,
  ContactMedium.MediumTypeEnum.User5Email,
] as const;
interface ResolvedContactSelection {
  dropdownContactId: string;
  contact?: Contact;
  customerParty?: NonNullable<CustomerResponse['relatedParty']>[number];
}

export interface BillingAccountCocoFormValues {
  billingAccount: {
    payerName: string;
    payerNameExtension: string;
    payerAddress: { countryCode: string; line1: string; postOffice: string; postalCode: string };
    billingContactId: string;
    billFormatType: BillFormat.IdEnum;
    billingAccountName: string;
    billingAccountExtensionName: string;
    customerReference1: string;
    customerReference2: string;
    customerReference3: string;
    customerReference4: string;
    billElectronicOperator: string;
    billElectronicAddress: string;
    billLanguage: string;
    deliveryMethod: BillDeliveryMethod.IdEnum;
    billReceiverType: ContactType;
    billReceiverName: string;
    billReceiverId: string;
    additionalBillReceiverEmails: string[];
  };
  billReceiverSelection: ReceiverType;
}

export interface BillingAccountCocoFormContext {
  billingAccountType: BillingAccountType;
  relatedParties: RelatedPartyRef[];
  customer?: CustomerResponse;
  contacts?: Contact[];
}

const getContactName = (contact?: Contact): string => {
  const firstName = contact?.person?.firstName ?? '';
  const lastName = contact?.person?.lastName ?? '';
  return [firstName, lastName].filter(Boolean).join(' ');
};

const getMediumValue = (
  contactMedium: ContactMedium[] | undefined,
  mediumType: ContactMedium.MediumTypeEnum,
  pick: (characteristic: MediumCharacteristic) => string | undefined
): string => {
  const medium = contactMedium?.find(candidate => candidate.mediumType === mediumType);
  return (medium && pick(medium.characteristic)) ?? '';
};

// `relatedParty[].id` is the contact master id, which is also what the create request expects.
export const toContactOptions = (relatedParties: RelatedParty[] = []): Contact[] =>
  relatedParties
    .filter(party => party.referredType === BillingAccountRelatedPartyRole.CONTACT)
    .map(party => ({
      contactId: party.id,
      contactMasterId: party.id,
      contactType: ContactType.PERSON,
      person: {
        firstName: party.givenName ?? party.fullName ?? party.name ?? '',
        lastName: party.familyName ?? '',
        email: getMediumValue(party.contactMedium, ContactMedium.MediumTypeEnum.email, c => c.emailAddress),
        phoneNumber: getMediumValue(party.contactMedium, ContactMedium.MediumTypeEnum.telephone, c => c.phoneNumber),
      },
    }));

const resolveContactSelection = (
  dropdownContactId: string,
  contacts: Contact[] = [],
  customer?: CustomerResponse
): ResolvedContactSelection => {
  const contact = contacts.find(candidate =>
    [candidate.contactId, candidate.contactMasterId].includes(dropdownContactId)
  );
  const customerPartyIds = new Set(
    [contact?.contactMasterId, contact?.contactId, dropdownContactId].filter((id): id is string => Boolean(id))
  );

  return {
    dropdownContactId,
    contact,
    customerParty: customer?.relatedParty?.find(party => customerPartyIds.has(party.id)),
  };
};

const buildContactParty = ({ contact, customerParty }: ResolvedContactSelection): RelatedPartyRef => {
  const contactId = contact?.contactMasterId ?? contact?.contactId ?? '';
  const name = customerParty?.name ?? getContactName(contact);

  return {
    id: customerParty?.id ?? contactId,
    name,
    role: BillingAccountRelatedPartyRole.CONTACT,
    referredType: customerParty?.referredType ?? BillingAccountRelatedPartyRole.CONTACT,
    ...(customerParty?.contactMedium ? { contactMedium: customerParty.contactMedium } : {}),
  };
};

// SFDC/CoCo billing accounts need the same customer represented twice: once as the
// invoice payer and once as the account owner — hence two RelatedPartyRef entries.
const buildOrganizationParties = (values: BillingAccountCocoFormValues, customer?: CustomerResponse) => {
  const engagedParty = customer?.engagedParty;
  const customerId = customer?.id ?? engagedParty?.id ?? values.billingAccount.payerName;
  const customerName = customer?.name ?? engagedParty?.name ?? values.billingAccount.payerName;
  const organizationIdentification = engagedParty?.organizationIdentification?.[0];
  const postalMedium = customer?.contactMedium?.find(
    medium => medium.mediumType === ContactMedium.MediumTypeEnum.postalAddress
  );
  const payer: RelatedPartyRef = {
    id: customerId,
    name: customerName,
    role: BillingAccountRelatedPartyRole.PAYER,
    type: 'Customer',
    engagedParty: {
      id: customerId,
      name: customerName,
      type: engagedParty?.referredType ?? CustomerType.Organization,
      organizationIdentification,
      ...(values.billingAccount.payerNameExtension
        ? { otherName: { name: values.billingAccount.payerNameExtension } }
        : {}),
      ...(postalMedium ? { contactMedium: [postalMedium] } : {}),
    },
  };

  const owner: RelatedPartyRef = {
    id: customerId,
    name: customerName,
    role: BillingAccountRelatedPartyRole.OWNER,
    referredType: engagedParty?.referredType ?? CustomerType.Organization,
  };

  return [payer, owner];
};

const buildAdditionalEmailMediums = (emails: string[]): ContactMedium[] =>
  emails.filter(Boolean).map((email, index) => ({
    mediumType: USER_EMAIL_MEDIUM_TYPES[index],
    type: 'EmailContactMedium',
    characteristic: { emailAddress: email },
  }));

const resolveEmailMedium = (party: RelatedPartyRef, emails: string[]): ContactMedium[] => [
  ...(party.contactMedium ?? []),
  ...buildAdditionalEmailMediums(emails),
];

const buildBillFormat = (billingAccount: BillingAccountCocoFormValues['billingAccount']): BillFormat => ({
  id: billingAccount.billFormatType,
  language: billingAccount.billLanguage,
  characteristic: [
    billingAccount.customerReference1,
    billingAccount.customerReference2,
    billingAccount.customerReference3,
    billingAccount.customerReference4,
  ].flatMap((value, index) => (value ? [{ name: `customerReference${index + 1}`, value }] : [])),
});

const buildRelatedParties = (
  values: BillingAccountCocoFormValues,
  context: BillingAccountCocoFormContext
): RelatedPartyRef[] => {
  const [payer, owner] = buildOrganizationParties(values, context.customer);
  const selectedContact = resolveContactSelection(
    values.billingAccount.billingContactId,
    context.contacts,
    context.customer
  );
  const contactParty = buildContactParty(selectedContact);
  const parties = [payer, owner, contactParty];

  if (values.billingAccount.deliveryMethod !== BillingAccountDeliveryMethod.EMAIL) {
    return parties;
  }

  const receiverContact =
    values.billReceiverSelection === ReceiverTypes.SAME_AS_CONTACT
      ? selectedContact
      : resolveContactSelection(values.billingAccount.billReceiverId, context.contacts, context.customer);
  const receiverParty = buildContactParty(receiverContact.contact ? receiverContact : selectedContact);
  return [
    ...parties,
    {
      ...receiverParty,
      role: BillingAccountRelatedPartyRole.BILL_RECEIVER,
      contactMedium: resolveEmailMedium(receiverParty, values.billingAccount.additionalBillReceiverEmails),
    },
  ];
};

const buildDeliveryMethod = (values: BillingAccountCocoFormValues): BillDeliveryMethod[] => {
  const { billingAccount } = values;
  const characteristics =
    billingAccount.deliveryMethod === BillingAccountDeliveryMethod.ELECTRONIC
      ? [
          { name: 'eOperatorId', value: billingAccount.billElectronicOperator },
          { name: 'eInvoiceAddress', value: billingAccount.billElectronicAddress },
        ]
      : [];

  return [
    {
      id: billingAccount.deliveryMethod,
      deliveryMethodType: billingAccount.deliveryMethod,
      characteristic: characteristics,
    },
  ];
};

export const toBillingAccountCreateRequestCoco = (
  values: BillingAccountCocoFormValues,
  context: BillingAccountCocoFormContext
): BillingAccountCreateRequestCoco => {
  return {
    name: values.billingAccount.billingAccountName,
    ...(values.billingAccount.billingAccountExtensionName
      ? { otherName: { name: values.billingAccount.billingAccountExtensionName } }
      : {}),
    billingAccountType: context.billingAccountType,
    relatedParty: buildRelatedParties(values, context),
    billStructure: { format: buildBillFormat(values.billingAccount) },
    billDeliveryMethod: buildDeliveryMethod(values),
  };
};
